/**
 * PARA ONDE VOLTAR depois de entrar ou criar conta.
 *
 * Quem clica em "Anunciar imóvel" deslogado é levado ao cadastro, e precisa
 * voltar para o anúncio depois — senão cria a conta e é despejado numa tela
 * qualquer, sem nenhuma pista de que faltava um passo.
 *
 * O destino viaja na URL (`?destino=/anunciar`), e não no `state` do roteador,
 * por dois motivos práticos: o cadastro tem duas etapas e troca de URL entre
 * elas, e um F5 no meio não pode apagar a intenção.
 *
 * O PREÇO DE ESTAR NA URL é que qualquer um escreve o que quiser ali. Um
 * `?destino=https://banco-falso.com.br` transformaria a tela de cadastro do
 * VENDOLOTE em trampolim de phishing — o link começa no domínio certo, com
 * cadeado e tudo, e termina em outro lugar. É a vulnerabilidade de
 * "redirecionamento aberto", e a defesa é não confiar no parâmetro.
 */

export const DESTINATION_PARAM = 'destino';

/** Limite generoso para uma rota interna; acima disso é abuso, não navegação. */
const TAMANHO_MAXIMO = 512;

/** Base usada quando não há navegador — testes rodam em Node. */
const ORIGEM_PADRAO = 'http://portal.invalido';

function origemAtual(): string {
  return typeof window === 'undefined' ? ORIGEM_PADRAO : window.location.origin;
}

/**
 * Devolve o destino APENAS se ele for um caminho interno deste portal.
 *
 * Aceita `/anunciar` e `/painel/anuncios?criado=STL-1`. Recusa endereço
 * absoluto (`https://…`), protocolo-relativo (`//site-falso`, que o navegador
 * resolve como OUTRO domínio apesar de parecer caminho), a variação com barra
 * invertida que alguns navegadores normalizam para a mesma coisa, e
 * `javascript:`.
 *
 * Quem decide se é interno é o PARSER DO PRÓPRIO NAVEGADOR, comparando a
 * origem resolvida com a nossa. Uma lista de prefixos proibidos escrita à mão
 * envelhece mal: cada navegador normaliza um punhado de grafias exóticas, e
 * basta uma escapar para a checagem virar decoração.
 */
export function safeDestination(raw: string | null | undefined, origem = origemAtual()): string | null {
  if (!raw) return null;

  const valor = raw.trim();

  if (valor.length === 0 || valor.length > TAMANHO_MAXIMO) return null;

  // Caminho interno começa com barra. Corta de saída `https://…` e
  // `javascript:…` sem depender do resto.
  if (!valor.startsWith('/')) return null;

  // Caractere de controle — tabulação, nova linha, nulo — abre espaço para
  // enganar o parser de URL, que silenciosamente ignora alguns deles ao
  // normalizar o endereço.
  for (const caractere of valor) {
    const codigo = caractere.codePointAt(0) ?? 0;
    if (codigo < 0x20 || codigo === 0x7f) return null;
  }

  try {
    const resolvida = new URL(valor, origem);
    if (resolvida.origin !== new URL(origem).origin) return null;

    return `${resolvida.pathname}${resolvida.search}${resolvida.hash}`;
  } catch {
    return null;
  }
}

/** Monta `?destino=…` já codificado, ou string vazia quando não há destino. */
export function destinationQuery(destino: string | null | undefined): string {
  const seguro = safeDestination(destino);
  return seguro ? `?${DESTINATION_PARAM}=${encodeURIComponent(seguro)}` : '';
}
